import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import LessonLockedNotice from "@/components/paywall/LessonLockedNotice";
import { getCourse, getLessons, type Course, type Lesson } from "@/lib/training-api";

interface CourseLessonRow {
  id: string;
  title: string;
  category: Lesson["category"] | null;
  duration_min: number | null;
  completed: boolean;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; course: Course; lessons: CourseLessonRow[] }
  | { kind: "error" };

export default function CoursePage() {
  const { t } = useTranslation();
  const { courseId } = useParams<{ courseId: string }>();
  const [loadState, setLoadState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    if (!courseId) return;
    let cancelled = false;
    (async () => {
      try {
        const [course, { lessons }] = await Promise.all([getCourse(courseId), getLessons()]);
        if (cancelled) return;
        if (!course) {
          setLoadState({ kind: "error" });
          return;
        }
        const byId = new Map(lessons.map((l) => [l.id, l]));
        // Keep the course's authored order; resolve titles/progress from the catalogue.
        const rows: CourseLessonRow[] = course.lesson_ids.map((id) => {
          const l = byId.get(id);
          return {
            id,
            title: l?.title ?? id.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
            category: l?.category ?? null,
            duration_min: l?.duration_min ?? null,
            completed: l?.completed ?? false,
          };
        });
        setLoadState({ kind: "ready", course, lessons: rows });
      } catch {
        if (!cancelled) setLoadState({ kind: "error" });
      }
    })();
    return () => { cancelled = true; };
  }, [courseId]);

  if (loadState.kind === "loading") {
    return (
      <LearnerPortalShell pageTitle={t("nav.courses")}>
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-8">
          <div className="space-y-4 animate-pulse">
            <div className="h-40 rounded-2xl bg-slate-200" />
            <div className="h-4 w-2/3 rounded bg-slate-200" />
            <div className="h-12 rounded-xl bg-slate-200" />
            <div className="h-12 rounded-xl bg-slate-200" />
          </div>
        </div>
      </LearnerPortalShell>
    );
  }

  if (loadState.kind === "error") {
    return (
      <LearnerPortalShell pageTitle={t("nav.courses")}>
        <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-8">
          <p className="text-lg font-semibold text-slate-700">{t("course.error.notFound")}</p>
          <Link to="/courses" className="mt-4 inline-block text-brand-primary underline">
            {t("course.backToCourses")}
          </Link>
        </div>
      </LearnerPortalShell>
    );
  }

  const { course, lessons } = loadState;
  const total = lessons.length;
  const completed = lessons.filter((l) => l.completed).length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <LearnerPortalShell pageTitle={course.title}>
      <div className="bg-slate-50 min-h-full">
        {/* Breadcrumb */}
        <div className="border-b border-slate-200 bg-white">
          <div className="mx-auto flex max-w-4xl items-center gap-2 px-4 py-3 text-sm sm:px-8">
            <Link to="/courses" className="text-brand-primary hover:underline no-underline">
              {t("nav.courses")}
            </Link>
            <span className="text-slate-400">/</span>
            <span className="font-medium text-slate-700 truncate">{course.title}</span>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-8">
          {/* Header */}
          <div className="flex items-start gap-4">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-primary/90 to-slate-800 text-4xl">
              {course.emoji ?? "📚"}
            </span>
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-bold text-slate-900 sm:text-3xl">{course.title}</h1>
              <p className="mt-1 text-base text-slate-600">{course.summary}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-slate-700">
                {t("course.progress.label", { completed, total })}
              </span>
              <span className="font-semibold text-brand-primary">{pct}%</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-brand-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
          </div>

          {/* Locked paywall (course gated) */}
          {course.locked && (
            <div className="mt-6">
              <LessonLockedNotice requiresUpgradeTo={course.requires_upgrade_to} />
            </div>
          )}

          {/* Ordered lessons */}
          <h2 className="mt-8 mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t("course.lessons.heading")}
          </h2>
          <ol className="space-y-2">
            {lessons.map((l, i) => {
              const Row = (
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3.5">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                      l.completed ? "bg-green-500 text-white" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {l.completed ? <Check aria-hidden="true" size={15} /> : i + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate font-medium text-slate-800">{l.title}</span>
                  {l.duration_min != null && (
                    <span className="shrink-0 text-xs text-slate-400">
                      {t("courses.card.durationMin", { minutes: l.duration_min })}
                    </span>
                  )}
                </div>
              );
              // A locked course shows the outline but doesn't deep-link into bodies.
              return (
                <li key={l.id}>
                  {course.locked ? (
                    <div className="opacity-70">{Row}</div>
                  ) : (
                    <Link to={`/training/${l.id}`} className="block no-underline transition-all hover:-translate-y-0.5">
                      {Row}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </LearnerPortalShell>
  );
}
